/**
 * src/pages/auth/ConfirmPage.tsx
 *
 * Magic-link confirmation interstitial. Public route, mounted ABOVE RequireAuth
 * (like /invite/:code) so an unauthenticated visitor arriving from an email link
 * actually reaches it.
 *
 * WHY THIS EXISTS: email link scanners (notably Gmail) prefetch links on delivery.
 * The magic-link email therefore points here (/auth/confirm?token=...) — an inert SPA
 * page — instead of directly at the one-time GET /api/auth/magic-link/verify endpoint.
 * A scanner's prefetch loads this page but never clicks, so the one-time token survives.
 * The token is only spent when a human clicks "Sign in", which navigates (a full page
 * load via a plain <a>, NOT client-side routing) to the verify endpoint → 302 + session
 * cookie → app root. The verify link lives only in this rendered page, which the email
 * scanner never sees.
 */
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { Logo } from '@/shared/ui/Logo';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';

const ConfirmPage: FC = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const reduced = useReducedMotion();
  const token = params.get('token');

  // Reconstruct the better-auth verify endpoint from the token. Plain <a> (not <Link>)
  // so the click is a real browser navigation to the Worker — required for the 302 +
  // Set-Cookie session handshake to land on the app root.
  const verifyUrl = token
    ? `/api/auth/magic-link/verify?token=${encodeURIComponent(token)}&callbackURL=${encodeURIComponent('/')}`
    : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans flex flex-col">
      <header className="h-20 flex items-center px-6 md:px-12">
        <Logo className="h-9 w-auto" />
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <motion.div
          className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-card p-6 flex flex-col gap-4 text-center"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduced ? 0 : 0.2 }}
        >
          <Mail size={32} className="mx-auto text-blue-600 dark:text-blue-400" aria-hidden="true" />
          {verifyUrl ? (
            <>
              <p className="text-subhead font-bold text-slate-900 dark:text-slate-100">
                {t('auth.confirm.title', 'Вход в Estimatix')}
              </p>
              <p className="text-body text-muted-foreground">
                {t('auth.confirm.body', 'Нажмите кнопку ниже, чтобы завершить вход.')}
              </p>
              <a
                href={verifyUrl}
                className="inline-flex items-center justify-center w-full rounded-input bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 text-body transition-colors focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400"
              >
                {t('auth.confirm.cta', 'Войти')}
              </a>
            </>
          ) : (
            <p className="text-body text-destructive" role="alert">
              {t('auth.confirm.invalid', 'Ссылка недействительна или устарела. Запросите новую.')}
            </p>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default ConfirmPage;
